import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Grid, Box, Button, TextField, InputAdornment, Card, Typography,
  Avatar, Chip, IconButton, Tooltip, Paper, Checkbox,
  Menu, MenuItem as MuiMenuItem,
} from "@mui/material";
import { useDeleteShopMutation, useGetShopQuery } from "../../../Api/attoDeskApi";
import { IShop } from "../../../Api/Interface/api.interface";
import NewPopUpCustomer from "../Edit/index";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import { CiSearch } from "react-icons/ci";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import VisibilitySharpIcon from "@mui/icons-material/VisibilitySharp";
import ReceiptIcon from "@mui/icons-material/Receipt";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import StorefrontIcon from "@mui/icons-material/Storefront";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Pagination from "../../../Components/Pagination";
import dayjs from "dayjs";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

interface EnrichedShop extends IShop {
  order_count: number;
  last_order_date: string | null;
  outstanding: number;
  credit_status: {
    credit_limit: number; outstanding: number; available: number | null;
    usage_percent: number; color: string; is_blocked: boolean;
    has_overdue: boolean; manually_blocked: boolean; credit_terms: string;
    overdue_count?: number; invoice_counts?: { total: number; unpaid: number; partial: number; paid: number };
  };
}

const TABS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "overdue", label: "Overdue" },
  { key: "blocked", label: "Blocked" },
  { key: "no_orders", label: "No Orders" },
];

type SortKey = "name" | "outstanding" | "order_count" | "last_order" | "credit_limit";

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const LARAVEL_API_URL = process.env.REACT_APP_LARAVEL_API;
  const { showErrorMessage, showMessage } = useNotifier();

  const [shops, setShops] = useState<EnrichedShop[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(50);
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const [selectedCoursing, setSelectedCoursing] = useState<IShop | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteCategory, setOpenDeleteCategory] = useState(false);
  const [coursingToDelete, setCoursingToDelete] = useState<IShop | null>(null);

  const getHeaders = () => {
    const t = localStorage.getItem("token") || "";
    return { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` };
  };

  const loadShops = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/shops/list-enriched`, { method: "POST", headers: getHeaders() });
      const data = await res.json();
      console.log("shops/list-enriched response:", data);
      if (data.success && data.data) {
        setShops(data.data);
      } else {
        // Fallback to basic shop list
        console.warn("Enriched endpoint failed, falling back to basic list");
        const fallback = await fetch(`${API_URL}/shop/getAll`, { method: "POST", headers: getHeaders(), body: JSON.stringify({ searchText: "", id: "" }) });
        const fbData = await fallback.json();
        if (fbData.data) {
          setShops(fbData.data.map((s: any) => ({ ...s, order_count: 0, last_order_date: null, outstanding: 0, credit_status: { credit_limit: 0, outstanding: 0, available: null, usage_percent: 0, color: "green", is_blocked: false, has_overdue: false, manually_blocked: false, credit_terms: "no_limit", invoice_counts: { total: 0, unpaid: 0, partial: 0, paid: 0 } } })));
        }
      }
    } catch (e) { console.error("loadShops error:", e); showErrorMessage("Failed to load restaurants"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadShops(); }, [loadShops]);

  const [deleteCategory] = useDeleteShopMutation();
  const handleDelete = async (id: string) => {
    try {
      const response = await deleteCategory(id).unwrap();
      if (response.status) { showMessage("Deleted successfully"); setOpenDeleteCategory(false); loadShops(); }
      else showErrorMessage("Failed to delete");
    } catch { showErrorMessage("Failed to delete"); }
  };

  const handleBlockToggle = async (shopId: number, block: boolean) => {
    try {
      const res = await fetch(`${API_URL}/shops/${shopId}/credit-settings`, {
        method: "PUT", headers: getHeaders(), body: JSON.stringify({ credit_blocked: block }),
      });
      const data = await res.json();
      if (data.success) { showMessage(block ? "Restaurant blocked" : "Restaurant unblocked"); loadShops(); }
    } catch { showErrorMessage("Failed"); }
  };

  // Filtering
  const filtered = useMemo(() => {
    let list = shops;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(s =>
        (s.customerCompanyName || "").toLowerCase().includes(q) ||
        (s.customerFirstName || "").toLowerCase().includes(q) ||
        (s.customerLastName || "").toLowerCase().includes(q) ||
        (s.customerMobileNum || "").toLowerCase().includes(q)
      );
    }
    if (statusFilter === "active") list = list.filter(s => !s.credit_status?.is_blocked && !s.credit_status?.has_overdue);
    else if (statusFilter === "overdue") list = list.filter(s => s.credit_status?.has_overdue);
    else if (statusFilter === "blocked") list = list.filter(s => s.credit_status?.is_blocked);
    else if (statusFilter === "no_orders") list = list.filter(s => s.order_count === 0);
    return list;
  }, [shops, searchQuery, statusFilter]);

  // Sorting
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let va: any, vb: any;
      switch (sortKey) {
        case "name": va = (a.customerCompanyName || "").toLowerCase(); vb = (b.customerCompanyName || "").toLowerCase(); break;
        case "outstanding": va = a.outstanding; vb = b.outstanding; break;
        case "order_count": va = a.order_count; vb = b.order_count; break;
        case "last_order": va = a.last_order_date || ""; vb = b.last_order_date || ""; break;
        case "credit_limit": va = a.credit_status?.credit_limit || 0; vb = b.credit_status?.credit_limit || 0; break;
        default: va = a.id; vb = b.id;
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return null;
    return sortDir === "asc" ? <ArrowUpwardIcon sx={{ fontSize: 11, ml: 0.25 }} /> : <ArrowDownwardIcon sx={{ fontSize: 11, ml: 0.25 }} />;
  };

  const paged = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Counts
  const counts = useMemo(() => ({
    total: shops.length,
    active: shops.filter(s => !s.credit_status?.is_blocked && !s.credit_status?.has_overdue).length,
    overdue: shops.filter(s => s.credit_status?.has_overdue).length,
    blocked: shops.filter(s => s.credit_status?.is_blocked).length,
    no_orders: shops.filter(s => s.order_count === 0).length,
  }), [shops]);

  const toggleSelect = (id: number) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => { if (selected.size === paged.length) setSelected(new Set()); else setSelected(new Set(paged.map(s => s.id))); };

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" height="10vh"><CircularProgress /></Box>;

  return (
    <Card className={classes.card}>
      <Grid container>
        <Grid item xs={12} className={classes.titleGrid} sx={{ px: 3, py: 2 }}>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 2 }}>
            <Typography className={classes.cardTitle}>Restaurants</Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField size="small" placeholder="Search name, phone..."
                value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><CiSearch style={{ fontSize: 18, color: "#999" }} /></InputAdornment>, classes: { root: classes.formControlList } }}
                sx={{ width: 260, "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate("/shop/create")}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, fontSize: 13, bgcolor: "#009688", "&:hover": { bgcolor: "#00796B" }, boxShadow: "0 2px 8px rgba(0,150,136,0.2)" }}>
                Add Restaurant
              </Button>
            </Box>
          </Box>

          {/* Summary Bar */}
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            {[
              { icon: <StorefrontIcon sx={{ fontSize: 16, color: "#1565C0" }} />, bg: "#E3F2FD", label: "Total", value: counts.total },
              { icon: <CheckCircleIcon sx={{ fontSize: 16, color: "#2E7D32" }} />, bg: "#E8F5E9", label: "Active", value: counts.active },
              { icon: <WarningAmberIcon sx={{ fontSize: 16, color: "#E65100" }} />, bg: "#FFF3E0", label: "Overdue", value: counts.overdue },
              { icon: <BlockIcon sx={{ fontSize: 16, color: "#C62828" }} />, bg: "#FFEBEE", label: "Blocked", value: counts.blocked },
            ].map(s => (
              <Paper key={s.label} elevation={0} sx={{ px: 2, py: 1, borderRadius: 2, border: "1px solid #eee", display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: s.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>{s.icon}</Box>
                <Box><Typography fontSize={10} color="#999" fontWeight={600} textTransform="uppercase">{s.label}</Typography><Typography fontSize={15} fontWeight={800}>{s.value}</Typography></Box>
              </Paper>
            ))}
          </Box>

          {/* Filter Tabs */}
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {TABS.map(t => {
              const count = (counts as any)[t.key === "all" ? "total" : t.key] ?? 0;
              return (
                <Chip key={t.key} label={`${t.label} (${count})`} onClick={() => { setStatusFilter(t.key); setPage(0); }} size="small"
                  sx={{ fontSize: 11, fontWeight: 600, cursor: "pointer", borderRadius: "8px", height: 28,
                    ...(statusFilter === t.key ? { bgcolor: "#009688", color: "#fff" } : { bgcolor: "#f5f5f5", color: "#666", "&:hover": { bgcolor: "#e0e0e0" } }) }} />
              );
            })}
          </Box>

          {/* Bulk Actions */}
          {selected.size > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1.5, p: 1.5, bgcolor: "#E0F2F1", borderRadius: 2 }}>
              <Typography fontSize={13} fontWeight={600} color="#009688">{selected.size} selected</Typography>
              <Button size="small" variant="contained" onClick={() => { Array.from(selected).forEach(id => handleBlockToggle(id, true)); setSelected(new Set()); }}
                sx={{ borderRadius: 2, textTransform: "none", fontSize: 11, fontWeight: 600, bgcolor: "#C62828", "&:hover": { bgcolor: "#B71C1C" } }}>Block Selected</Button>
              <Button size="small" variant="outlined" onClick={() => setSelected(new Set())}
                sx={{ borderRadius: 2, textTransform: "none", fontSize: 11, color: "#999" }}>Clear</Button>
            </Box>
          )}
        </Grid>

        <Grid item xs={12}>
          <TableContainer>
            <Table className={classes.table}>
              <TableHead className={classes.tableHeaderRow}>
                <TableRow>
                  <TableCell className={classes.tablheader} sx={{ width: 36, p: 1 }}>
                    <Checkbox size="small" checked={selected.size === paged.length && paged.length > 0}
                      indeterminate={selected.size > 0 && selected.size < paged.length}
                      onChange={selectAll} sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: 18 } }} />
                  </TableCell>
                  <TableCell className={classes.tablheader} sx={{ cursor: "pointer" }} onClick={() => toggleSort("name")}>
                    <Box sx={{ display: "flex", alignItems: "center" }}><strong>Restaurant</strong><SortIcon k="name" /></Box>
                  </TableCell>
                  <TableCell className={classes.tablheader}><strong>Contact</strong></TableCell>
                  <TableCell className={classes.tablheader} sx={{ cursor: "pointer" }} onClick={() => toggleSort("outstanding")}>
                    <Box sx={{ display: "flex", alignItems: "center" }}><strong>Outstanding</strong><SortIcon k="outstanding" /></Box>
                  </TableCell>
                  <TableCell className={classes.tablheader} sx={{ cursor: "pointer" }} onClick={() => toggleSort("order_count")}>
                    <Box sx={{ display: "flex", alignItems: "center" }}><strong>Orders</strong><SortIcon k="order_count" /></Box>
                  </TableCell>
                  <TableCell className={classes.tablheader} sx={{ cursor: "pointer" }} onClick={() => toggleSort("credit_limit")}>
                    <Box sx={{ display: "flex", alignItems: "center" }}><strong>Credit</strong><SortIcon k="credit_limit" /></Box>
                  </TableCell>
                  <TableCell className={classes.tablheader}><strong>Status</strong></TableCell>
                  <TableCell className={classes.tablheader} sx={{ textAlign: "right" }}><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>No restaurants found</TableCell></TableRow>
                ) : paged.map((row) => {
                  const cs = row.credit_status;
                  const statusLabel = cs?.has_overdue ? "Overdue" : cs?.is_blocked ? "Blocked" : cs?.color === "orange" ? "Near Limit" : "Good";
                  const statusColor = cs?.has_overdue || cs?.is_blocked ? "#C62828" : cs?.color === "orange" ? "#E65100" : "#2E7D32";
                  const statusBg = cs?.has_overdue || cs?.is_blocked ? "#FFEBEE" : cs?.color === "orange" ? "#FFF3E0" : "#E8F5E9";

                  return (
                    <TableRow key={row.id} sx={{ "&:hover": { bgcolor: "#fafafa" }, bgcolor: cs?.has_overdue ? "#FFF8F6" : cs?.is_blocked ? "#FFF8F6" : undefined }}>
                      <TableCell className={classes.tablCell} sx={{ p: 1 }}>
                        <Checkbox size="small" checked={selected.has(row.id)} onChange={() => toggleSelect(row.id)} sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: 18 } }} />
                      </TableCell>
                      <TableCell className={classes.tablCell}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, cursor: "pointer" }} onClick={() => navigate(`/shop-history/${row.id}`)}>
                          <Box sx={{ width: 36, height: 36, borderRadius: 1.5, overflow: "hidden", flexShrink: 0, bgcolor: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {row.customerImg ? (
                              <img src={`${LARAVEL_API_URL}/${row.customerImg}`} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            ) : (
                              <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#999" }}>{(row.customerCompanyName || row.customerFirstName || "R").charAt(0).toUpperCase()}</Typography>
                            )}
                          </Box>
                          <Box>
                            <Typography fontSize={13} fontWeight={600} color="#333" sx={{ "&:hover": { color: "#009688" } }}>
                              {row.customerCompanyName || [row.customerFirstName, row.customerLastName].filter(Boolean).join(" ")}
                            </Typography>
                            {row.customerCompanyName && (row.customerFirstName || row.customerLastName) && (
                              <Typography fontSize={11} color="#999">{[row.customerFirstName, row.customerLastName].filter(Boolean).join(" ")}</Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell className={classes.tablCell}>
                        <Typography fontSize={12}>{row.customerMobileNum || "—"}</Typography>
                        <Typography fontSize={11} color="#999" noWrap sx={{ maxWidth: 160 }}>{row.customerEmail || ""}</Typography>
                      </TableCell>
                      <TableCell className={classes.tablCell}>
                        <Typography fontSize={13} fontWeight={700} color={row.outstanding > 0 ? "#E65100" : "#2E7D32"}>
                          ${row.outstanding.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.tablCell}>
                        <Typography fontSize={13} fontWeight={600}>{row.order_count}</Typography>
                        {row.last_order_date && (
                          <Typography fontSize={10} color="#999">{dayjs(row.last_order_date).format("MMM D")}</Typography>
                        )}
                      </TableCell>
                      <TableCell className={classes.tablCell}>
                        {cs?.credit_limit > 0 ? (
                          <Box>
                            <Typography fontSize={12} fontWeight={600}>${cs.credit_limit.toFixed(0)}</Typography>
                            {/* Mini progress */}
                            <Box sx={{ width: 60, height: 4, bgcolor: "#f0f0f0", borderRadius: 2, mt: 0.25 }}>
                              <Box sx={{ height: "100%", width: `${Math.min(100, cs.usage_percent)}%`, bgcolor: cs.color === "red" ? "#C62828" : cs.color === "orange" ? "#E65100" : "#2E7D32", borderRadius: 2 }} />
                            </Box>
                          </Box>
                        ) : (
                          <Typography fontSize={12} color="#ccc">No limit</Typography>
                        )}
                      </TableCell>
                      <TableCell className={classes.tablCell}>
                        <Chip label={statusLabel} size="small"
                          sx={{ fontSize: 10, fontWeight: 700, bgcolor: statusBg, color: statusColor, height: 22, borderRadius: "11px" }} />
                      </TableCell>
                      <TableCell className={classes.tablCell} sx={{ textAlign: "right" }}>
                        <Box sx={{ display: "flex", gap: 0.25, justifyContent: "flex-end" }}>
                          <Tooltip title="View Profile">
                            <IconButton size="small" onClick={() => navigate(`/shop-history/${row.id}`)} sx={{ color: "#999", "&:hover": { color: "#009688" } }}>
                              <VisibilitySharpIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Invoices">
                            <IconButton size="small" onClick={() => navigate(`/shop-history/${row.id}`, { state: { tab: "Invoices" } })} sx={{ color: "#999", "&:hover": { color: "#1565C0" } }}>
                              <ReceiptIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => { setSelectedCoursing(row); setOpenDialog(true); }} sx={{ color: "#999", "&:hover": { color: "#009688" } }}>
                              <EditSharpIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={cs?.is_blocked ? "Unblock" : "Block"}>
                            <IconButton size="small" onClick={() => handleBlockToggle(row.id, !cs?.is_blocked)} sx={{ color: "#999", "&:hover": { color: cs?.is_blocked ? "#2E7D32" : "#C62828" } }}>
                              {cs?.is_blocked ? <CheckCircleIcon sx={{ fontSize: 17 }} /> : <BlockIcon sx={{ fontSize: 17 }} />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => { setCoursingToDelete(row); setOpenDeleteCategory(true); }} sx={{ color: "#999", "&:hover": { color: "#C62828" } }}>
                              <DeleteOutlineSharpIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Grid spacing={5} p="18px" py={4} justifyContent="end" container textAlign="center" alignContent="center" alignItems="center">
            <Grid sx={{ textAlign: "end", justifyContent: "flex-end", alignItems: "flex-end" }}>
              <Pagination count={sorted.length} page={page} rowsPerPage={rowsPerPage} onPageChange={(_: any, p: number) => setPage(p)} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {selectedCoursing && <NewPopUpCustomer openModel={openDialog} handleCloseDialog={() => { setSelectedCoursing(null); setOpenDialog(false); loadShops(); }} data={selectedCoursing} />}
      {coursingToDelete && (
        <DeletePopup open={openDeleteCategory} handleCloseDelete={() => { setCoursingToDelete(null); setOpenDeleteCategory(false); }}
          onConfirm={async () => { await handleDelete(coursingToDelete.id.toString()); }}
          title="Delete Restaurant" content={`Are you sure you want to delete "${coursingToDelete.customerCompanyName || [coursingToDelete.customerFirstName, coursingToDelete.customerLastName].filter(Boolean).join(" ")}"?`} />
      )}
    </Card>
  );
};

export default Index;
